/**
 * Timezone helpers. Streaks, calendars and "clean days" all depend on the user's
 * local day, so conversions must be explicit (see docs/DECISIONS.md#adr-011).
 *
 * Uses Intl only — no date library. Requires a full-ICU Node build, which the
 * official node:20 images are. `time.test.ts` fails loudly if that ever changes.
 */

/** Offset in ms between the given instant as rendered in `timeZone` and real UTC. */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);

  const f: Record<string, number> = {};
  for (const p of parts) if (p.type !== 'literal') f[p.type] = Number(p.value);

  const asUtc = Date.UTC(
    f.year!,
    f.month! - 1,
    f.day!,
    f.hour! === 24 ? 0 : f.hour!,
    f.minute!,
    f.second!,
  );
  return asUtc - date.getTime();
}

/**
 * Converts a wall-clock time in `timeZone` to the matching UTC instant.
 * Resolves DST shifts by re-checking the offset at the candidate instant.
 */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const firstGuess = naive - zoneOffsetMs(new Date(naive), timeZone);
  const corrected = naive - zoneOffsetMs(new Date(firstGuess), timeZone);
  return new Date(corrected);
}

/** The user's local calendar day (`YYYY-MM-DD`) for a given instant. */
export function localDateOf(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);

  const f: Record<string, string> = {};
  for (const p of parts) if (p.type !== 'literal') f[p.type] = p.value;
  return `${f.year}-${f.month}-${f.day}`;
}

/** The hour of day (0-23) an instant falls on in the user's timezone. */
export function localHourOf(instant: Date, timeZone: string): number {
  const value = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    hour12: false,
  }).format(instant);
  // Some locales render midnight as "24"; normalise it back to 0.
  return Number(value) % 24;
}

/**
 * Day of week, 0 = Monday … 6 = Sunday.
 *
 * Monday-first because the calendar and the charts are Spanish; using
 * JavaScript's Sunday-first numbering here would leak into every consumer.
 */
export function localWeekdayOf(instant: Date, timeZone: string): number {
  const { year, month, day } = parseLocalDate(localDateOf(instant, timeZone));
  return weekdayOfLocalDate(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}

/** Same numbering as `localWeekdayOf`, straight from a `YYYY-MM-DD` string. */
export function weekdayOfLocalDate(date: string): number {
  const { year, month, day } = parseLocalDate(date);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

/** Parses `YYYY-MM-DD` into its numeric parts. Throws on malformed input. */
export function parseLocalDate(date: string): { year: number; month: number; day: number } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`Invalid local date: ${date}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/** Parses `HH:MM` into its numeric parts. Throws on malformed input. */
export function parseClockTime(time: string): { hour: number; minute: number } {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m) throw new Error(`Invalid time: ${time}`);
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) throw new Error(`Invalid time: ${time}`);
  return { hour, minute };
}

/** Adds whole days to a `YYYY-MM-DD` string, staying in calendar space. */
export function addDays(date: string, days: number): string {
  const { year, month, day } = parseLocalDate(date);
  const d = new Date(Date.UTC(year, month - 1, day));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Whole days between two `YYYY-MM-DD` strings (`to - from`). */
export function daysBetween(from: string, to: string): number {
  const a = parseLocalDate(from);
  const b = parseLocalDate(to);
  const ms =
    Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day);
  return Math.round(ms / 86_400_000);
}
